// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useToasts } from "react-toast-notifications";
import { useQuery } from "react-query";
import ticketService from "services/ticketService";
import TicketBarcodeInput from "./TicketBarcodeInput";
import TicketImageField from "./TicketImageField";
import TicketSaleAssignmentFields from "./TicketSaleAssignmentFields";
import {
  buildTicketAssigneesPayload,
  mapTicketRowToAssigneeSelections,
  validateTicketAssigneeSelections,
} from "./ticketSaleAssignmentUtils";
import {
  extractTicketUploadResourceId,
  mapTicketTypeOptions,
  normalizeReceiveBusOrderRefCode,
  resolveSendToSaleOrderRefCode,
} from "./utils";

const selectProps = {
  className: "basic-single w-100",
  classNamePrefix: "select2-selection",
};

/** Dropdown renders in portal so options are not clipped inside Modal body */
const asyncModalMenuProps =
  typeof document !== "undefined"
    ? {
        menuPortalTarget: document.body,
        styles: {
          menuPortal: base => ({ ...base, zIndex: 2050 }),
        },
      }
    : {};

const buildInitialFormValue = ticket => {
  const existingResources = Array.isArray(ticket?.resources)
    ? ticket.resources
        .map(item => {
          const id = Number(item?.id);
          if (!Number.isInteger(id) || id <= 0) {
            return null;
          }
          return {
            id,
            name: String(item?.file_name ?? ""),
          };
        })
        .filter(Boolean)
    : [];

  return {
    ticketCode: String(ticket?.code ?? ""),
    orderRefCode: String(ticket?.order_ref_code ?? ""),
    receiveBusOrderRefCode: String(ticket?.receive_bus_order_ref_code ?? ""),
    ticketName: null,
    note: String(ticket?.note ?? ""),
    assigneeSelections: mapTicketRowToAssigneeSelections(ticket),
    existingResources,
    newImages: [],
  };
};

const TicketSendSaleModal = props => {
  const { isOpen, toggle, ticket, onConfirm, isSubmitting = false } = props;
  const { t } = useTranslation("ticketManagement");
  const { addToast } = useToasts();
  const [formValue, setFormValue] = useState(() => buildInitialFormValue(ticket));
  const [formErrors, setFormErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  const { data: ticketTypesRes, isLoading: isLoadingTicketTypes } = useQuery(
    ["ticket_types_ajax_send_sale"],
    () => ticketService.listTicketTypes(),
    {
      enabled: isOpen,
      staleTime: 60_000,
      retry: 1,
    }
  );

  const ticketTypeOptions = useMemo(
    () => mapTicketTypeOptions(ticketTypesRes?.data),
    [ticketTypesRes]
  );

  useEffect(() => {
    if (isOpen) {
      const nextFormValue = buildInitialFormValue(ticket);
      const currentName = String(ticket?.name ?? "").trim();
      const selectedTicketNameOption =
        ticketTypeOptions.find(option => option.label === currentName) || null;
      setFormValue({
        ...nextFormValue,
        ticketName: selectedTicketNameOption,
      });
      setFormErrors({});
    }
  }, [isOpen, ticket, ticketTypeOptions]);

  const isFormLocked = isSubmitting || isUploading;

  const handleChangeField = (field, value) => {
    setFormValue(prev => ({ ...prev, [field]: value }));
    setFormErrors(prev => ({ ...prev, [field]: "" }));
  };

  const handleAddImages = files => {
    setFormErrors(prev => ({ ...prev, images: "" }));
    handleChangeField("newImages", [...(formValue.newImages || []), ...files]);
  };

  const handleRemoveExistingResource = resourceId => {
    handleChangeField(
      "existingResources",
      (formValue.existingResources || []).filter(item => item.id !== resourceId)
    );
  };

  const handleRemoveNewImage = removedIndex => {
    handleChangeField(
      "newImages",
      (formValue.newImages || []).filter((_, index) => index !== removedIndex)
    );
  };

  const uploadImagesToResourceIds = async files => {
    const ids = [];
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      const data = new FormData();
      data.append("file", file);
      const res = await ticketService.uploadTicketResource(data);
      const rid = extractTicketUploadResourceId(res);

      if (!Number.isInteger(rid) || rid <= 0) {
        throw new Error("upload_resource_id");
      }

      ids.push(rid);
    }
    return ids;
  };

  const handleConfirm = async () => {
    const assigneeErrors = validateTicketAssigneeSelections({
      assigneeSelections: formValue.assigneeSelections,
      requiredMessage: t("saleAssignment.requiredAssignees"),
    });

    const totalImageCount =
      (formValue.existingResources?.length || 0) + (formValue.newImages?.length || 0);
    const nextErrors = { ...assigneeErrors };

    if (totalImageCount === 0) {
      nextErrors.images = t("toast.createSelectImage");
    }

    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }

    const { assignees } = buildTicketAssigneesPayload({
      assigneeSelections: formValue.assigneeSelections,
    });

    const existingResourceIds = (formValue.existingResources || [])
      .map(item => Number(item?.id))
      .filter(id => Number.isInteger(id) && id > 0);

    let uploadedResourceIds = [];
    try {
      setIsUploading(true);
      const files = formValue.newImages || [];
      if (files.length > 0) {
        uploadedResourceIds = await uploadImagesToResourceIds(files);
      }
    } catch {
      addToast(t("toast.createUploadFailed"), {
        appearance: "error",
        autoDismiss: true,
      });
      setIsUploading(false);
      return;
    }

    const resourceIds = [...existingResourceIds, ...uploadedResourceIds];
    const selectedTypeId = Number(formValue.ticketName?.value);
    setIsUploading(false);
    onConfirm?.({
      ticket,
      assignees,
      typeId: Number.isInteger(selectedTypeId) && selectedTypeId > 0 ? selectedTypeId : undefined,
      note: String(formValue.note || "").trim(),
      resourceIds,
      orderRefCode: resolveSendToSaleOrderRefCode(ticket, formValue.orderRefCode),
      receiveBusOrderRefCode: normalizeReceiveBusOrderRefCode(formValue.receiveBusOrderRefCode),
    });
  };

  const code = ticket?.code ?? "";
  const totalImageCount =
    (formValue.existingResources?.length || 0) + (formValue.newImages?.length || 0);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      trapFocus={false}
      contentClassName="ticket-modal-compact"
    >
      <ModalHeader toggle={toggle}>
        {t("sendSale.title")}
        {code ? ` — ${code}` : ""}
      </ModalHeader>
      <ModalBody className="pt-2 pb-1">
        <Row>
          <Col md={12}>
            <FormGroup className="mb-2">
              <TicketBarcodeInput
                id="ticket-send-sale-ref-code"
                label="Mã tham chiếu"
                value={formValue.orderRefCode}
                placeholder={t("sendSale.orderRefCodePlaceholder")}
                onChange={value => handleChangeField("orderRefCode", value)}
                disabled={isFormLocked}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <FormGroup className="mb-2">
              <Label>Tên ticket</Label>
              <Select
                {...selectProps}
                {...asyncModalMenuProps}
                options={ticketTypeOptions}
                value={formValue.ticketName}
                onChange={option => handleChangeField("ticketName", option)}
                placeholder={
                  isLoadingTicketTypes ? "Đang tải danh sách..." : "Chọn loại ticket"
                }
                isSearchable={false}
                isLoading={isLoadingTicketTypes}
                isDisabled={isFormLocked || ticketTypeOptions.length === 0}
              />
            </FormGroup>
          </Col>
        </Row>

        <TicketSaleAssignmentFields
          isOpen={isOpen}
          isDisabled={isFormLocked}
          formValue={formValue}
          formErrors={formErrors}
          onFieldChange={handleChangeField}
        />

        <FormGroup className="mb-2">
          <Label>
            Ảnh ticket (tối đa 2 ảnh) <span className="text-danger">*</span>
          </Label>
          <TicketImageField
            isDisabled={isFormLocked}
            errorMessage={formErrors.images}
            maxCount={2}
            selectedCount={totalImageCount}
            newFiles={formValue.newImages || []}
            existingItems={formValue.existingResources || []}
            onAddFiles={handleAddImages}
            onRemoveNewFile={handleRemoveNewImage}
            onRemoveExistingItem={handleRemoveExistingResource}
          />
        </FormGroup>

        <FormGroup className="mb-2">
          <TicketBarcodeInput
            id="ticket-send-sale-receive-bus-ref"
            label="Mã đơn lấy hàng"
            value={formValue.receiveBusOrderRefCode}
            placeholder={t("sendSale.receiveBusOrderRefCodePlaceholder")}
            onChange={value => handleChangeField("receiveBusOrderRefCode", value)}
            disabled={isFormLocked}
          />
        </FormGroup>

        <FormGroup className="mb-0">
          <Label>Mô tả</Label>
          <Input
            type="textarea"
            rows={2}
            value={formValue.note}
            onChange={event => handleChangeField("note", event.target.value)}
            disabled={isFormLocked}
            placeholder={t("sendSale.notePlaceholder")}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={isFormLocked}>
          {t("sendSale.cancel")}
        </Button>
        <Button color="primary" onClick={handleConfirm} disabled={isFormLocked}>
          {t("sendSale.confirm")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TicketSendSaleModal;
